import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import { FinancialDetailsFormFields } from "@/modules/property-tracks/hooks/rental-financial-proposal/multi-step-form.hook";
import { Box, Divider, Typography } from "@mui/material";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import FinancialShare from "./financial-share";
import FinancialCostSharePayments from "./financial-cost-share-payments";

interface FinancialDetailsFormProps {
  form: UseFormReturn<FinancialDetailsFormFields>;
  totalAmount: number;
}

export default function FinancialDetailsForm({
  form,
  totalAmount,
}: FinancialDetailsFormProps) {
  const clientCostSharePercent = useWatch({
    control: form.control,
    name: "clientCostSharePercent",
  }) || 0;

  const clientCostShare = totalAmount * (clientCostSharePercent / 100);

  return (
    <Box>
      <Typography
        sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
      >
        Rental details
      </Typography>
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
      >
        <Controller
          control={form.control}
          name={"rentalAmount"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Rental amount"
              placeholder="Rental amount"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>EGP</Typography>,
                },
              }}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"annualIncreasePercent"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Annual increase"
              placeholder="Annual increase"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>%</Typography>,
                },
              }}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"rentalYears"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Number of years"
              placeholder="Number of years"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>years</Typography>,
                },
              }}
            />
          )}
        />
      </Box>
      <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />

      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Cost & revenue splitting
        </Typography>
        <FinancialShare
          form={form}
          field={"clientCostSharePercent"}
          title={"Cost share split"}
        />
        <FinancialShare
          form={form}
          field={"clientRevenueSharePercent"}
          title={"Revenue share split"}
        />
      </Box>

      <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />

      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Payment plan
        </Typography>
        <FinancialCostSharePayments
          form={form}
          clientTotalCostShare={clientCostShare}
        />
      </Box>
    </Box>
  );
}
